import { useCallback, useEffect, useMemo } from 'react';

import { useUser } from '@clerk/react';
import { ClerkMetadataKey } from '@this+that/utils';
import log from 'loglevel';

import { useAreIntegrationsReady } from './Integrations/useAreIntegrationsReady';
import { useStableArray } from './useStableArray';
import { IntegrationAccount } from '../models/integrationAccount';
import { useInboxAccountFilterStore } from '../store/Inbox/AccountFilter';
import { updateUnsafeMetadata } from '../utils/clerkMetadata/updateUnsafeMetadata';

export const useInboxAccountFilter = (
  integrationAccounts: IntegrationAccount[],
) => {
  const { user } = useUser();
  const { selectedAccountIds, setSelectedAccountIds } =
    useInboxAccountFilterStore();

  const allIds = useStableArray(
    useMemo(
      () => integrationAccounts.map((acc) => acc.displayName),
      [integrationAccounts],
    ),
  );

  const areIntegrationsReady = useAreIntegrationsReady();
  // An empty list is only authoritative once fetched; before that it is just un-hydrated.
  const isAccountListKnown = allIds.length > 0 || areIntegrationsReady;

  useEffect(() => {
    if (!isAccountListKnown) {
      return;
    }
    let cancelled = false;

    const syncInboxAccountsFilterMetadata = async (ids: string[]) => {
      if (!user) {
        return;
      }
      try {
        await updateUnsafeMetadata(user, {
          [ClerkMetadataKey.INBOX_ACCOUNT_FILTER]: ids,
        });
      } catch (error) {
        log.error('Failed to sync account filter to metadata', { error });
      }
    };

    const stored =
      user?.unsafeMetadata?.[ClerkMetadataKey.INBOX_ACCOUNT_FILTER];
    const storedIsEmpty =
      !Array.isArray(stored) ||
      stored.length === 0 ||
      !stored.every((item): item is string => typeof item === 'string');

    const handleAllAccountsRemoved = async () => {
      const isSelectionAlreadyEmpty = selectedAccountIds?.length === 0;
      if (!isSelectionAlreadyEmpty) {
        setSelectedAccountIds([]);
      }
      if (!storedIsEmpty && !cancelled) {
        await syncInboxAccountsFilterMetadata([]);
      }
    };

    const handleStaleSelection = async () => {
      if (selectedAccountIds === null) {
        return;
      }
      const hasStaleIds = selectedAccountIds.some((id) => !allIds.includes(id));
      if (!hasStaleIds) {
        return;
      }
      const validIds = selectedAccountIds.filter((id) => allIds.includes(id));
      const newIds = validIds.length > 0 ? validIds : allIds;
      setSelectedAccountIds(newIds);
      if (!cancelled) {
        await syncInboxAccountsFilterMetadata(newIds);
      }
    };

    const handleInitialLoad = async () => {
      if (!storedIsEmpty) {
        const normalizeId = (id: string): string => {
          const account = integrationAccounts.find(
            (acc) => acc.identifier === id || acc.displayName === id,
          );
          return account?.displayName ?? id;
        };
        const normalizedStored = stored.map(normalizeId);
        const validIds = normalizedStored.filter((id) => allIds.includes(id));
        const newIds = validIds.length > 0 ? validIds : allIds;
        setSelectedAccountIds(newIds);
        if (validIds.length !== stored.length && !cancelled) {
          await syncInboxAccountsFilterMetadata(newIds);
        }
      } else {
        setSelectedAccountIds(allIds);
      }
    };

    const run = async () => {
      if (allIds.length === 0) {
        return handleAllAccountsRemoved();
      }
      if (selectedAccountIds !== null) {
        return handleStaleSelection();
      }
      return handleInitialLoad();
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [
    user,
    isAccountListKnown,
    allIds,
    selectedAccountIds,
    integrationAccounts,
    setSelectedAccountIds,
  ]);

  const effectiveSelectedIds = useStableArray(selectedAccountIds ?? allIds);

  const saveToMetadata = useCallback(
    (ids: string[]) => {
      if (!user) {
        return;
      }
      updateUnsafeMetadata(user, {
        [ClerkMetadataKey.INBOX_ACCOUNT_FILTER]: ids,
      }).catch((error: unknown) =>
        log.error('Failed to save account filter to metadata', { error }),
      );
    },
    [user],
  );

  const handleToggleAccount = useCallback(
    (id: string) => {
      const newIds = effectiveSelectedIds.includes(id)
        ? effectiveSelectedIds.filter((a) => a !== id)
        : [...effectiveSelectedIds, id];
      setSelectedAccountIds(newIds);
      saveToMetadata(newIds);
    },
    [effectiveSelectedIds, setSelectedAccountIds, saveToMetadata],
  );

  const handleSelectAllAccounts = useCallback(() => {
    setSelectedAccountIds(allIds);
    saveToMetadata(allIds);
  }, [allIds, setSelectedAccountIds, saveToMetadata]);

  const handleToggleAllAccounts = useCallback(() => {
    const newIds = effectiveSelectedIds.length === allIds.length ? [] : allIds;
    setSelectedAccountIds(newIds);
    saveToMetadata(newIds);
  }, [effectiveSelectedIds, allIds, setSelectedAccountIds, saveToMetadata]);

  const fetchEmailFilters = useStableArray(
    useMemo<string[] | undefined>(() => {
      if (allIds.length <= 1) {
        return undefined;
      }
      if (effectiveSelectedIds.length === allIds.length) {
        return undefined;
      }
      return effectiveSelectedIds;
    }, [allIds, effectiveSelectedIds]),
  );

  return {
    selectedAccountIds: effectiveSelectedIds,
    fetchEmailFilters,
    handleToggleAccount,
    handleSelectAllAccounts,
    handleToggleAllAccounts,
  };
};
